import { useMemo, useState } from 'react'
import { isEditor } from '../utils/misc'
import { useScroll } from '../utils/hooks'
import { ResponsiveImage } from './ResponsiveImage'
import { Icon } from './Icon'
import { LucideIconName } from '@bm-js/atlascloud-shared'
import { NavPage, NavProps } from '../utils/types'

export const Nav1 = <PAGE extends NavPage>({
  contactInfo,
  logoWide,
  navTree,
  scrollElement,
  setActivePageFromNav,
  LinkComponent,
}: NavProps<PAGE>) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const { isScrolled } = useScroll(scrollElement)

  const positionClass = useMemo(() => {
    if (isEditor()) return 'absolute'
    return 'fixed'
  }, [])

  const breakpointClasses = useMemo(() => {
    let desktopNavLinks = 'md:flex'
    let hamburger = 'md:hidden'

    if (isEditor()) {
      desktopNavLinks = '@2xl/top:flex'
      hamburger = '@2xl/top:hidden'
    }

    return {
      desktopNavLinks,
      hamburger,
    }
  }, [])

  return (
    <div
      className={`${positionClass} top-0 left-0 right-0 z-50 flex items-center justify-between py-4 px-4 transition-all duration-300 ${
        isScrolled ? 'bg-black/40 backdrop-blur-sm' : ''
      }`}
    >
      {/* Logo */}
      <div className='flex items-center space-x-2 w-14'>
        <LinkComponent href='/'>
          {logoWide && <ResponsiveImage file={logoWide} />}
        </LinkComponent>
      </div>
      <nav className='flex items-center justify-between bg-transparent text-white'>
        <div
          className={`${breakpointClasses.desktopNavLinks} hidden items-center space-x-6 bg-customerPrimary-400 text-customerInverse px-4 py-2 rounded-full text-sm`}
        >
          {navTree.map((page) => (
            <LinkComponent
              href={page.slug}
              key={page.uuid}
              onClick={() => setActivePageFromNav?.(page)}
            >
              {page.viewName}
            </LinkComponent>
          ))}

          {contactInfo?.phone && (
            <LinkComponent
              href={`tel:${contactInfo?.phone}`}
              className='font-semibold'
            >
              {contactInfo?.phone}
            </LinkComponent>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`${breakpointClasses.hamburger} px-3 py-1 rounded-full flex items-center bg-customerPrimary-400 text-customerInverse`}
          onClick={() => setMenuOpen(true)}
        >
          <Icon name={LucideIconName.Menu} className='size-6' />
        </button>

        {/* Mobile Overlay. */}
        <div
          className={`absolute bg-black/80 backdrop-blur-sm h-screen text-white flex items-center w-full text-2xl top-0 bottom-0 transition-all duration-300 ${
            menuOpen ? 'right-0 blur-0' : '-right-[110%] blur-sm'
          }`}
        >
          <button
            className='absolute top-6 right-6'
            onClick={() => setMenuOpen(false)}
          >
            <Icon name={LucideIconName.X} className='size-6' />
          </button>
          <div className='flex flex-col items-end gap-8 w-full px-6 mb-20'>
            {navTree.map((page) => (
              <LinkComponent
                href={page.slug}
                key={page.uuid}
                onClick={() => {
                  setMenuOpen(false)
                  setActivePageFromNav?.(page)
                }}
              >
                {page.viewName}
              </LinkComponent>
            ))}
            {contactInfo?.phone && (
              <a href={`tel:${contactInfo?.phone}`}>{contactInfo?.phone}</a>
            )}
          </div>
        </div>
      </nav>
    </div>
  )
}
